import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import { currentTableSlice } from './currentTable';

/**
 * Multi-table reducer.
 *
 * Wraps the existing currentTable reducer so that each gameID gets its own
 * independent state tree.  Actions are routed by `action.meta.gameID`.
 *
 *   state.tables = {
 *     "12345": { game: {...}, gameState: {...}, players: [...], ... },
 *     "67890": { game: {...}, gameState: {...}, players: [...], ... },
 *   }
 */

type TableState = ReturnType<typeof currentTableSlice.reducer>;

// Store the playerGUID at module level so new tables can inherit it.
let _playerGUID: string | null = null;

const tablesSlice = createSlice({
  name: 'tables',
  initialState: {} as Record<string, TableState>,
  reducers: {
    /** Remove a table's state when the player leaves */
    removeTable: (state, action: PayloadAction<string>) => {
      delete state[action.payload];
    }
  },
  extraReducers: (builder) => {
    const actionTypes = Object.values(currentTableSlice.actions).map((a) => a.type);

    // Capture playerGUID whenever it's dispatched (even before tables exist)
    builder.addMatcher(
      (action) =>
        action.type === currentTableSlice.actions.updateCurrentPlayerGUID.type &&
        !action.meta?.gameID,
      (state, action) => {
        _playerGUID = action.payload;
        // Broadcast to all existing tables
        for (const gameID of Object.keys(state)) {
          state[gameID] = currentTableSlice.reducer(state[gameID], action);
        }
      }
    );

    // Actions with meta.gameID → route to that specific table
    builder.addMatcher(
      (action) => actionTypes.includes(action.type) && action.meta?.gameID,
      (state, action) => {
        const gameID = action.meta.gameID as string;
        const scopedPlayerGUID = action.meta.playerGUID;

        if (!state[gameID]) {
          // New table — seed with playerGUID so the reducer can identify the player
          const initial = currentTableSlice.getInitialState();
          state[gameID] = {
            ...initial,
            currentPlayer: { ...initial.currentPlayer, playerGUID: scopedPlayerGUID || _playerGUID }
          } as TableState;
        } else if (
          // Only an identity the action carries may repair an existing table.
          // `_playerGUID` is module state shared by every store, including the
          // hand-history snapshot stores that dispatch their demo viewer.
          scopedPlayerGUID &&
          state[gameID].currentPlayer.playerGUID !== scopedPlayerGUID
        ) {
          // Repair stale table-scoped identity before deriving footer seat state.
          state[gameID] = currentTableSlice.reducer(
            state[gameID],
            currentTableSlice.actions.updateCurrentPlayerGUID(scopedPlayerGUID)
          );
        }
        state[gameID] = currentTableSlice.reducer(state[gameID], action);
      }
    );

    // Broadcast other global actions to ALL tables
    builder.addMatcher(
      (action) =>
        [
          currentTableSlice.actions.updateCurrentPlayerBankAmount.type,
          currentTableSlice.actions.updateTournamentStatus.type,
          currentTableSlice.actions.mergeTournamentStatus.type
        ].includes(action.type) && !action.meta?.gameID,
      (state, action) => {
        for (const gameID of Object.keys(state)) {
          state[gameID] = currentTableSlice.reducer(state[gameID], action);
        }
      }
    );
  }
});

export const { removeTable } = tablesSlice.actions;
export type { TableState };
export default tablesSlice.reducer;
